import { z } from 'zod';
import type { SourceGrade } from './contratto';
import type { PhaseLevelDto, PhaseQuestionDto, PhaseQuestionsDto } from './fase0';
import type { StepTimelineDto, WizardStepKey } from './onboarding';

/**
 * Le fasi 1 «L'impresa», 2 «Le sedi» e 3 «Le attività» del contratto n. 1 versione 2.0 (art. 8; ADR 0174), nella forma
 * delle pagine di prova `docs/mockup/percorso-sinapsi/04-06`: ogni voce con il «perché?», la fonte e il grado; i
 * bloccanti; lo scarto; il livello con ciò che manca; le domande del registro; «Parliamone».
 */

/** Le fasi con la pagina del servizio delle fasi (la fase 0 ha la sua, ADR 0171). */
export type PhaseNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
export type PhaseWave = 'A' | 'B' | 'C' | 'D' | 'E';

/**
 * Il registro delle fasi del contratto n. 1 (art. 8; ADR 0174): l'ordine, il titolo della colonna, il passo della
 * configurazione in cui vive la pagina, l'ondata (art. 8, «Le ondate») e le fasi che
 * eredita (riga «Eredita»). È il punto unico in cui una fase nuova si dichiara; la colonna, i tempi e le attese lo leggono.
 */
export interface PhaseInfo {
  n: number;
  titolo: string;
  passo: WizardStepKey;
  ondata: PhaseWave;
  eredita: number[];
}
export const PHASES: readonly PhaseInfo[] = [
  { n: 0, titolo: 'Conosco l’azienda', passo: 'visura', ondata: 'A', eredita: [] },
  { n: 1, titolo: 'L’impresa', passo: 'impresa', ondata: 'A', eredita: [0] },
  { n: 2, titolo: 'Le sedi', passo: 'sedi', ondata: 'B', eredita: [0, 1] },
  { n: 3, titolo: 'Le attività', passo: 'attivita', ondata: 'B', eredita: [0, 1, 2] },
  { n: 4, titolo: 'I processi', passo: 'processi', ondata: 'C', eredita: [0, 1, 2, 3] },
  { n: 5, titolo: 'Gli impianti', passo: 'impianti', ondata: 'C', eredita: [0, 1, 2, 3] },
  { n: 6, titolo: 'Gli obblighi', passo: 'obblighi', ondata: 'D', eredita: [0, 1, 2, 3, 4, 5] },
  { n: 7, titolo: 'Programmi e piattaforme', passo: 'software', ondata: 'D', eredita: [3, 4, 5, 6] },
  { n: 8, titolo: 'Organizzazione e ruoli', passo: 'organizzazione', ondata: 'E', eredita: [0, 1, 2, 3, 4, 5, 6, 7] },
  { n: 9, titolo: 'Le procedure', passo: 'procedure', ondata: 'E', eredita: [3, 4, 5, 6, 7, 8] },
  { n: 10, titolo: 'La diagnosi', passo: 'diagnosi', ondata: 'E', eredita: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] },
  { n: 11, titolo: 'Proposte e percorso', passo: 'proposte', ondata: 'E', eredita: [10] },
];
/** Il tempo massimo di ogni ondata con una sede, in secondi (art. 10.3); ogni sede in più aggiunge 2 minuti. */
export const WAVE_SECONDS: Record<PhaseWave, number> = { A: 180, B: 300, C: 300, D: 420, E: 600 };
export const phaseInfo = (n: number): PhaseInfo | undefined => PHASES.find((p) => p.n === n);
/** Il passo della configurazione in cui vive ogni fase (art. 3.13). */
export const PHASE_STEP: Partial<Record<PhaseNumber, WizardStepKey>> = Object.fromEntries(PHASES.filter((p) => p.n > 0 && p.passo).map((p) => [p.n, p.passo]));
export const PHASE_OF_STEP: Partial<Record<WizardStepKey, PhaseNumber>> = Object.fromEntries(PHASES.filter((p) => p.n > 0 && p.passo).map((p) => [p.passo, p.n]));

/** Il grado della conclusione di una voce (art. 3.37). */
export const CONCLUSION_GRADES = ['certo', 'probabile', 'possibile'] as const;
export type ConclusionGrade = (typeof CONCLUSION_GRADES)[number];

/** Una spilla di fonte: la fonte per nome, il grado, la citazione e l'indirizzo web (art. 3.27, 3.30, 3.31). */
export interface PhaseSourceChipDto {
  testo: string;
  grado: SourceGrade | null;
  citazione: string | null;
  url: string | null;
}

/** Un numero in testa alla pagina, con la fonte (o «da confermare»). */
export interface PhaseFigureDto {
  etichetta: string;
  valore: string;
  fonte: string;
  /** Il dato manca o non basta: si mostra «da confermare». */
  da_confermare: boolean;
}

/** Un bloccante della fase, controllato dallo studio (art. 9.1). */
export interface PhaseCheckDto {
  cosa: string;
  ok: boolean;
}

/** Una riga dello scarto della fase (art. 8, «Lo scarto nasce dove nasce la voce»; art. 3.40). */
export interface PhaseScrapDto {
  voce: string;
  testo: string;
  /** ha / coperta / manca, o nessuno scarto. */
  oggi: 'ha' | 'coperta' | 'manca' | 'nessuno';
  /** Da cosa lo si sa, in parole. */
  perche: string | null;
}

/** «Da dove viene»: quante voci per strato (art. 6.2). */
export interface PhaseLayerCountDto {
  strato: string;
  voci: number;
}

export interface PhaseCommonDto {
  fase: number;
  /** si_prepara: non ancora partita; in_preparazione; pronta; errore (il consulente non ha consegnato). */
  stato: 'si_prepara' | 'in_preparazione' | 'pronta' | 'errore';
  /** Il nome dell'azienda come lo dice il titolare. */
  azienda: string | null;
  livello: PhaseLevelDto | null;
  /** Il lettore e il contatore della fase (art. 10.4): in corso, o chiuso come «Come l'ho preparata». */
  tempi: StepTimelineDto | null;
  /** Il tempo massimo dell'ondata della fase, con le sue sedi (art. 10.3). */
  ondata: { nome: PhaseWave; massimo_s: number; sedi: number };
  bloccanti: PhaseCheckDto[];
  /** Le domande nate in questa fase (art. 7-bis.4-5). */
  domande: PhaseQuestionDto[];
  limiti: PhaseQuestionsDto['limiti'];
  registro: PhaseQuestionsDto['registro'];
  scarto: PhaseScrapDto[];
  provenienza: PhaseLayerCountDto[];
  /** Ciò che manca, in parole semplici (art. 7.10): anche le affermazioni tolte perché senza fonte (art. 9.1). */
  manca: string[];
  /** «Non va bene» già usato: la fase si rifà una sola volta (art. 7-bis.8). */
  rifatta: boolean;
  /** Cosa è cambiato e dove: dopo una risposta, una correzione o «Non va bene» (art. 7-bis.6, 7-bis.14). */
  cambiato: string[];
  /** La data della conferma del titolare (art. 4.1, 3.138), se ha confermato. */
  confermata: string | null;
  /** La consegna mostrata: quando è stata preparata e quanto ci ha messo (art. 10.3), anche se un giro dopo è fallito. */
  consegnata_il?: string | null;
  fatta_in_s?: number | null;
  /** L'ultimo giro non è riuscito e la pagina mostra la consegna di prima: la testa lo dice, con la causa. */
  ultimo_giro?: { esito: 'non_riuscito'; causa: string; quando: string } | null;
  /** Le novità del metodo che lo studio sta aggiungendo a questa fase consegnata, senza rifarla: solo la loro sezione aspetta. */
  novita_in_corso?: string[];
}

/** Una frase scritta dal consulente, con le spille delle sue fonti; `consulente` = «secondo il consulente» (art. 3.28). */
export interface PhaseSentenceDto {
  testo: string;
  fonti: number[];
  consulente: boolean;
}

export interface PhaseOneDto extends PhaseCommonDto {
  fase: 1;
  numeri: PhaseFigureDto[];
  /** La classe dimensionale UE, calcolata dallo studio con le imprese collegate (art. 8, fase 1, studio; art. 3.76). */
  classe: {
    da_sola: string | null;
    da_sola_perche: string;
    collegate: Array<{ nome: string; codice_fiscale: string | null; legame: string; fonte: string; dati: string | null }>;
    /** La classe che vale, con il gruppo; null = da confermare. */
    vale: string | null;
    perche: string;
    /** Il dato che manca, quando la classe è «da confermare». */
    manca: string | null;
  };
  cosa_fa: { frasi: PhaseSentenceDto[]; fonti: PhaseSourceChipDto[]; perche: string };
  a_chi_vende: { imprese: boolean | null; privati: boolean | null; pubblica_amministrazione: boolean | null; dove: string | null };
  gruppo: { capogruppo: { nome: string; codice_fiscale: string | null } | null; quota: string | null; dal: string | null; nota: string } | null;
  contratti: Array<{ codice: string | null; nome: string; a_chi: string | null; grado: 'T' | 'scelta_timonio' | 'probabile'; perche: string | null }>;
  /** I conti letti (art. 3.56, 5.14): indici con la formula, fatti della nota integrativa, confronto, cosa dicono i numeri. */
  conti: {
    /** Il gradino del bilancio che ha dato i numeri (art. 5.4): 1 depositato, 2 dati online con l'anno, 3 da confermare. */
    gradino: 1 | 2 | 3;
    gradino_testo: string;
    indici: Array<{ nome: string; formula: string; esercizi: string; trend: string; valore: string; da_confermare: boolean; nota: string | null }>;
    fatti: Array<{ nome: string; valore: string | null; esercizio: string | null; citazione: string | null }>;
    settore: Array<{ rapporto: string; azienda: string; settore: string; fonte: string | null }>;
    numeri: Array<{ testo: string; fonte: string | null }>;
  } | null;
}

/** Un attributo di una sede (art. 3.82; tabella della fase 2). */
export interface PhaseSiteAttributeDto {
  peso: 'a' | 'b' | 'c';
  gruppo: string;
  /** Cosa risulta, in parole. */
  valore: string;
  esito: 'trovato' | 'stima' | 'da_confermare' | 'non_trovato' | 'non_si_applica' | 'fase_dopo';
  /** La fonte o il perché, breve (es. «DPR 151/2011, all. I n. 71 · A», «catasto non collegato»). */
  fonte: string;
  grado: SourceGrade | null;
}

export interface PhaseSiteDto {
  /** L'id della sede (tabella delle sedi). */
  id: string;
  /** «Sede legale · Bari», «Unità locale n. 2 · Modugno». */
  nome: string;
  tipo: 'legale' | 'operativa' | 'interna' | null;
  uso: string | null;
  indirizzo: string | null;
  indirizzo_fonte: string;
  addetti: number | null;
  addetti_fonte: string;
  attivita: string | null;
  attivita_fonte: string | null;
  insegna: string;
  /** Il «perché?» del tipo e dell'uso. */
  perche: string | null;
  /** Una nota della sede (es. la contraddizione degli addetti mostrata nella fase 0). */
  nota: string | null;
  attributi: PhaseSiteAttributeDto[];
  grado: ConclusionGrade | null;
  /** La sede l'ha aggiunta o corretta il titolare: resta sua (art. 7-bis.7). */
  del_titolare: boolean;
}

export interface PhaseTwoDto extends PhaseCommonDto {
  fase: 2;
  numeri: PhaseFigureDto[];
  sedi: PhaseSiteDto[];
  addetti_senza_sede: { numero: number; perche: string };
  /** L'esito della ricerca di altre sedi online (art. 8, fase 2, consegna). */
  altre_sedi: string;
  /** Le fonti dei gruppi di attributi ancora da collegare (registro delle fonti). */
  da_collegare: string[];
}

export interface PhaseActivityDto {
  id: string;
  nome_standard: string;
  nome_azienda: string | null;
  codice: string | null;
  prevalente: boolean;
  dove: string;
  per_chi: Array<'imprese' | 'privati' | 'pubblica_amministrazione'>;
  stato: 'esercitata' | 'non_esercitata' | 'da_confermare';
  fonti: string[];
  peso_ricavi: string | null;
  grado: ConclusionGrade | null;
}

export interface PhaseThreeDto extends PhaseCommonDto {
  fase: 3;
  numeri: PhaseFigureDto[];
  attivita: PhaseActivityDto[];
  servizi_online: Array<{ indirizzo_web: string; testo: string; cosa_fa: 'vende' | 'prenota' | 'assiste' | 'informa'; fonte: string }>;
  /** Quando non ce n'è: «nessuno trovato con la partita IVA». */
  servizi_nota: string | null;
  /** Ciò che il consulente non chiede e perché (es. un codice «da confermare» che non cambia il progetto). */
  non_chiesto: string | null;
}

export type PhaseDto = PhaseOneDto | PhaseTwoDto | PhaseThreeDto;

/** «Confermo la fase» (art. 4.1, 3.138) e «Non va bene» con il motivo (art. 7-bis.8). */
export const PhaseConfirmSchema = z.object({}).strict();

/** Una riga dell'«ho capito così» (art. 3.142; allegato, parte C.1). */
export interface PhaseTalkRowDto {
  tipo: 'risposta' | 'correzione' | 'aggiunta' | 'fatto_nuovo' | 'fuori_progetto';
  /** La voce o la domanda toccata, come la legge il titolare. */
  voce: string;
  fatto: string;
  cosa_cambierebbe: string;
}

export interface PhaseTalkDto {
  id: string;
  fase: number;
  testo: string;
  stato: 'in_lettura' | 'da_confermare' | 'confermato' | 'lasciato' | 'fuori_progetto' | 'errore';
  righe: PhaseTalkRowDto[];
  /** Dopo il sì: cosa è cambiato e dove. */
  cambiato: string[];
  il: string;
}

/** Il titolare scrive in «Parliamone» da una pagina di fase (art. 7-bis.14). */
export const PhaseTalkSchema = z.object({
  fase: z.number().int().min(0).max(11),
  testo: z.string().trim().min(2).max(4000),
});
export type PhaseTalkInput = z.infer<typeof PhaseTalkSchema>;

/** Il titolare risponde all'«ho capito così»: sì, oppure lascia stare («lo riscrivo» è un nuovo testo). */
export const PhaseTalkReplySchema = z.object({ esito: z.enum(['si', 'lascia']) });
export type PhaseTalkReplyInput = z.infer<typeof PhaseTalkReplySchema>;
